import { useMutation } from '@apollo/client';
import { zodResolver } from '@hookform/resolvers/zod';
import clsx from 'clsx';
import { useSnackbar } from 'notistack';
import { FC, useCallback, useMemo } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import type { SubmitHandler } from 'react-hook-form/dist/types/form';
import { z } from 'zod';

import { gql } from '../../../../generated';
import { SECTIONS_IDS } from '../../../constants/sections';
import { useCommonApolloOptions } from '../../../hooks/use-common-apollo-options';
import { useLkBasePath } from '../../../hooks/use-lk-base-path';
import { WithClassName } from '../../../types/react';
import { getErrors } from '../../../utils/apollo';
import { ArrowsForm1V4Icon } from '../../icons/ArrowsForm1V4Icon';
import { ArrowsForm2V4Icon } from '../../icons/ArrowsForm2V4Icon';
import { ArrowsForm3V4Icon } from '../../icons/ArrowsForm3V4Icon';
import { ArrowsForm4V4Icon } from '../../icons/ArrowsForm4V4Icon';
import { ButtonLink } from '../common/ButtonLink';
import { Checkbox } from '../common/Checkbox';
import { Container } from '../common/Container';
import { Control } from '../common/Control';
import { Flex } from '../common/Flex';
import { FormSubmitButton } from '../common/FormSubmitButton';
import { FormTextInput } from '../common/FormTextInput';
import { Text } from '../common/Text';
import { TextLink } from '../common/TextLink';

import styles from './FeedbackBlock.module.scss';

type FeedbackBlockProps = WithClassName & {};

const useFormSchema = () => {
  return useMemo(
    () =>
      z.object({
        name: z.string().min(1, {
          message: 'Field is required',
        }),
        email: z.string().email(),
        phone: z.string(),
        currency: z.string(),
        business_sector: z.string(),
        comment: z.string(),
        privacyConsent: z
          .boolean()
          .refine((v) => v, { message: 'You must accept the processing of your personal data' }),
      }),
    []
  );
};

type FeedbackFormData = z.TypeOf<ReturnType<typeof useFormSchema>>;

type FeedbackFormHandler = SubmitHandler<FeedbackFormData>;

const ADVANTAGES = [
  {
    icon: <ArrowsForm1V4Icon />,
    title: 'Compliance-first mindset',
  },
  {
    icon: <ArrowsForm2V4Icon />,
    title: 'Partner network',
  },
  {
    icon: <ArrowsForm3V4Icon />,
    title: 'Dedicated support',
  },
  {
    icon: <ArrowsForm4V4Icon />,
    title: 'Transparent operations ',
  },
];

const SendFeedback = gql(/* GraphQL */ `
  mutation SendFeedback($feedbackForm: FeedbackForm!) {
    sendInfoToGoogleSheet(feedbackForm: $feedbackForm)
  }
`);

export const FeedbackBlock: FC<FeedbackBlockProps> = (props) => {
  const { className } = props;

  const lkBasePath = useLkBasePath();
  const commonApolloOptions = useCommonApolloOptions();
  const { enqueueSnackbar } = useSnackbar();

  const [sendFeedback] = useMutation(SendFeedback, {
    ...commonApolloOptions,
  });

  const form = useForm<FeedbackFormData>({
    defaultValues: {
      name: '',
      email: '',
      phone: '',
      currency: '',
      business_sector: '',
      comment: '',
      privacyConsent: false,
    },
    resolver: zodResolver(useFormSchema()),
    mode: 'onBlur',
    reValidateMode: 'onChange',
  });

  const { register, handleSubmit, reset, control } = form;

  const onSubmit = useCallback<FeedbackFormHandler>(
    async (data) => {
      const { errors } = await sendFeedback({
        variables: {
          feedbackForm: {
            name: data.name,
            email: data.email,
            phone: data.phone,
            currency: data.currency,
            business_sector: data.business_sector,
            comment: data.comment,
          },
        },
      });

      if (getErrors(errors).length) {
        return;
      }

      enqueueSnackbar('Form has been submitted', {
        variant: 'success',
      });
      reset();
    },
    [enqueueSnackbar, reset, sendFeedback]
  );

  return (
    <Container className={clsx(styles.root, className)} id={SECTIONS_IDS.contact}>
      <Flex className={styles.inner} direction="column">
        <Text className={styles.title}>
          A payments partner
          <br />
          you can rely on
        </Text>

        <div className={styles.grid}>
          <Flex direction="column" gap={6}>
            <Text type="regular16" color="white50">
              If you have any questions let us know
            </Text>

            <FormProvider {...form}>
              <form className={styles.form} onSubmit={handleSubmit(onSubmit)}>
                <div className={styles.formColumns}>
                  <div className={styles.formColumn}>
                    <FormTextInput {...register('name')} required placeholder="Full name" />

                    <FormTextInput {...register('email')} required type="email" placeholder="Email" />

                    <FormTextInput {...register('phone')} type="tel" placeholder="Phone number" />
                  </div>

                  <div className={styles.formColumn}>
                    <FormTextInput {...register('currency')} placeholder="Currency (e.g. USD)" />

                    <FormTextInput {...register('business_sector')} placeholder="Industry" />
                  </div>
                </div>

                <FormTextInput {...register('comment')} placeholder="Comment" multiline rows={5} />

                <Controller
                  name="privacyConsent"
                  control={control}
                  render={({ field, fieldState }) => (
                    <Control className={styles.consent} error={fieldState.error?.message}>
                      <Checkbox
                        className={styles.consentCheckbox}
                        inputId="feedback-privacy-consent"
                        inputName={field.name}
                        inputRef={field.ref}
                        alignLabel="start"
                        checked={field.value}
                        labelType="regular12"
                        onBlur={field.onBlur}
                        onChange={field.onChange}
                        label={
                          <>
                            I agree to the processing of my personal data, as described in the{' '}
                            <TextLink
                              className={styles.privacyLink}
                              href="/doc/privacy"
                              target="_blank"
                              rel="noopener noreferrer"
                              type="regular12"
                              variant="white-50p"
                            >
                              Privacy policy
                            </TextLink>
                          </>
                        }
                      />
                    </Control>
                  )}
                />

                <FormSubmitButton>Send</FormSubmitButton>
              </form>
            </FormProvider>
          </Flex>

          <Flex className={styles.rightColumn} direction="column" gap={8} justifyContent="spaceBetween">
            <Flex direction="column" gap={10}>
              {ADVANTAGES.map(({ icon, title }, index) => (
                <Flex key={index} gap={6} alignItems="center">
                  {icon}

                  <Text type="medium18">{title}</Text>
                </Flex>
              ))}
            </Flex>

            <ButtonLink href={`${lkBasePath}/registration`} isOutsideLink>
              Sign up
            </ButtonLink>
          </Flex>
        </div>
      </Flex>
    </Container>
  );
};
